import { Ref, useCallback, useContext } from 'react';

import responseChangeStateButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/response_change_state_button_click';
import resumeInviteButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/resume_invite_button_click';
import { Button, Link, TooltipHover, Text } from '@hh.ru/magritte-ui';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { formatToReactComponent } from 'bloko/common/trl';

import { MarkAsViewedEvent } from 'Modules/Notices';
import ButtonWithSubstates from 'src/components/ChangeTopicButton/ButtonWithSubstates';
import MagritteInfotip from 'src/components/Infotip/MagritteInfotip';
import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import translation from 'src/components/translation';
import { useIsResumeSearchSource } from 'src/hooks/useIsResumeSearchSource';
import { useSelector } from 'src/hooks/useSelector';
import { NegotiationLink, TransitionType } from 'src/models/employerNegotiations/negotiationLinks';

const TrlKeys = {
    invite: 'vacancyResponse.invite',
    politeness: 'vacancy-response.hints.actions.content',
    politenessLink: 'vacancy-response.hints.actions.index',
    archivedTip: 'vacancy-response.hints.archive.actions.invite',
    changeTopic: 'employer.candidates.buttons.changeTopic',
    hiringManagerTooltip: 'employer.responses.hiringManager.tooltip',
};

interface Props {
    inviteUrl: string;
    inviteToAnotherUrl: string;
    inviteLinks?: NegotiationLink[];
    isMobile?: boolean;
}

const InviteButton: TranslatedComponent<Props> = ({ inviteUrl, inviteToAnotherUrl, inviteLinks, isMobile, trls }) => {
    const {
        isResponse,
        isChangeState,
        isFirstResponse,
        isGiftedResume,
        lastEmployerStateExtName,
        resume: { topicId },
        vacancyId,
    } = useContext(ResumeCardActionsContext);
    const isHiringManager = useSelector((state) => state.isHiringManager);

    const isArchived = useSelector((state) => state.vacancyResponseFlags.isArchived);
    const isReadyForChange = isChangeState && !isArchived;
    const isFromResumeSearch = useIsResumeSearchSource();

    const inEmployerSubstatesExp = useSelector((state) => state.isEmployerSubstatesExp);
    const inResponseCollection = isResponse && !isArchived;
    const showSubstates = inEmployerSubstatesExp && inResponseCollection && !!inviteLinks?.length;

    const sendAnalytics = useCallback(() => {
        if (isReadyForChange && lastEmployerStateExtName && topicId) {
            responseChangeStateButtonClick({
                vacancyId,
                topicId,
                folderName: lastEmployerStateExtName,
            });
            return;
        }
        resumeInviteButtonClick({
            vacancyId,
            topicId,
            folderName: lastEmployerStateExtName || 'unknown',
            gifted: isGiftedResume,
        });
    }, [isGiftedResume, lastEmployerStateExtName, isReadyForChange, topicId, vacancyId]);

    const buttonText = trls[isReadyForChange ? TrlKeys.changeTopic : TrlKeys.invite];

    return (
        <MagritteInfotip
            name={isArchived ? 'archive_vacancy_invite_tip' : '24_hours_reminder'}
            render={({ activatorRef }) => (
                <div ref={activatorRef as Ref<HTMLDivElement>}>
                    {showSubstates ? (
                        <ButtonWithSubstates
                            type={isReadyForChange ? TransitionType.ChangeTopic : TransitionType.Invite}
                            links={inviteLinks}
                            sendAnalytics={sendAnalytics}
                            stretched={!!isMobile}
                        />
                    ) : (
                        <Button
                            stretched={isMobile}
                            size="small"
                            mode="secondary"
                            Element={SPALink}
                            to={isArchived ? inviteToAnotherUrl : inviteUrl}
                            target={isFromResumeSearch ? '_blank' : undefined}
                            onClick={sendAnalytics}
                            data-qa="employee-invite-on-topic"
                        >
                            {buttonText}
                        </Button>
                    )}
                    {activatorRef && isHiringManager && (
                        <TooltipHover activatorRef={activatorRef} placement="top-center">
                            {trls[TrlKeys.hiringManagerTooltip]}
                        </TooltipHover>
                    )}
                </div>
            )}
            showBottomSheetOnMobile
            placement="top-right"
            showClose
            closeByClickOutside={false}
            visible={isFirstResponse}
            markAsViewedEvent={MarkAsViewedEvent.OnHide}
            useDefaultHost
        >
            <Text typography="paragraph-2-regular">
                {isArchived
                    ? trls[TrlKeys.archivedTip]
                    : formatToReactComponent(trls[TrlKeys.politeness], {
                          '{}': (
                              <Link
                                  style={isMobile ? 'accent' : 'contrast'}
                                  typography="paragraph-2-regular"
                                  Element={SPALink}
                                  to="/article/23734"
                                  target="_blank"
                                  inline
                              >
                                  {trls[TrlKeys.politenessLink]}
                              </Link>
                          ),
                      })}
            </Text>
        </MagritteInfotip>
    );
};

export default translation(InviteButton);
